import type { CSSProperties, ReactNode, Ref } from 'react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import OdinTransition from '../common/odin-transition/odin-transition.component';
import { getAllElementStyleValues } from '../common-functions';

export enum ToastLocation {
  TopLeft,
  TopRight,
  BottomLeft,
  BottomRight,
  Center,
}

export interface ToastWrapperProps {
  visible?: boolean;
  position?: ToastLocation;
  appendTo?: HTMLElement;
  transitionDuration?: number;
  timeout?: number; // in milliseconds
  showTransitionOptions?: string;
  hideTransitionOptions?: string;
  shouldCheckZIndex?: boolean;
  shown?: () => void;
  hidden?: () => void;
  children: ReactNode | Array<ReactNode>;
}

export interface ToastWrapperRef {
  show: () => void;
}

function ToastWrapper(props: ToastWrapperProps, ref: Ref<ToastWrapperRef>) {
  const {
    visible = false,
    position = ToastLocation.BottomRight,
    appendTo = document.body,
    transitionDuration = 400,
    timeout = 5000,
    showTransitionOptions = 'cubic-bezier(0, 0, 0.2, 1)',
    hideTransitionOptions = 'linear',
    shouldCheckZIndex = false,
    shown,
    hidden,
    children,
  } = props;

  const [visibility, setVisibility] = useState(visible);
  const [zIndex, setZIndex] = useState(-1);
  const [displayZIndex, setDisplayZIndex] = useState(100);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>();

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
    };
  }, []);

  useEffect(() => {
    if (visible !== undefined) {
      setVisibility(visible);
      if (visible) {
        startVisibilityTimer();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- startVisibilityTimer is stable (uses timeout from closure)
  }, [visible]);

  useEffect(() => {
    if (shouldCheckZIndex) {
      const allZIndexes = getAllElementStyleValues('zIndex', (styleValue) => {
        const elementZIndex = parseInt(styleValue);
        return elementZIndex >= 100;
      }).map((item) => parseInt(item));

      if (allZIndexes.length > 0) {
        setDisplayZIndex(Math.max(...allZIndexes) + 1);
      }
    }
  }, [shouldCheckZIndex]);

  const startVisibilityTimer = () => {
    if (timeout > 0) {
      timeoutRef.current = setTimeout(() => setVisibility(false), timeout);
    }
  };

  const show = () => {
    setVisibility(true);
    startVisibilityTimer();
  };

  useImperativeHandle(ref, () => ({
    show,
  }));

  const onEntering = () => {
    setZIndex(displayZIndex);
  };

  const onEntered = () => {
    if (shown) {
      shown();
    }
  };

  const onExited = () => {
    setZIndex(-1);
    if (hidden) {
      hidden();
    }
  };

  const addPositionStyles = (location: ToastLocation) => {
    let styles: CSSProperties = {};

    switch (location) {
      case ToastLocation.BottomRight:
        styles = {
          bottom: '12px',
          right: '12px',
        };
        break;
      case ToastLocation.TopRight:
        styles = {
          top: '12px',
          right: '12px',
        };
        break;
      case ToastLocation.BottomLeft:
        styles = {
          bottom: '12px',
          left: '12px',
        };
        break;
      case ToastLocation.TopLeft:
        styles = {
          top: '12px',
          left: '12px',
        };
        break;
      case ToastLocation.Center:
        styles = {
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        };
        break;
    }

    return styles;
  };

  const createToast = () => {
    const baseStyles = {
      ...addPositionStyles(position),
      zIndex,
    };

    return (
      <OdinTransition
        start={visibility}
        timeout={transitionDuration}
        showTransitionOptions={showTransitionOptions}
        hideTransitionOptions={hideTransitionOptions}
        onEntering={onEntering}
        onEntered={onEntered}
        onExited={onExited}
        className="odin-fixed"
        style={baseStyles}
      >
        {children}
      </OdinTransition>
    );
  };

  return ReactDOM.createPortal(createToast(), appendTo);
}

export default forwardRef(ToastWrapper);
